import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface User {
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load user from localStorage on app initialization
  useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedTimestamp = localStorage.getItem('authTimestamp');
        
        if (storedUser && storedTimestamp) {
          const timestamp = parseInt(storedTimestamp);
          const now = Date.now();
          const hoursSinceLogin = (now - timestamp) / (1000 * 60 * 60);
          
          // Check if session is still valid (24 hours)
          if (hoursSinceLogin < 24) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
          } else {
            // Session expired, clear storage
            localStorage.removeItem('user');
            localStorage.removeItem('authTimestamp');
          }
        }
      } catch (error) {
        console.error('Error loading user from storage:', error);
        // Clear corrupted data
        localStorage.removeItem('user');
        localStorage.removeItem('authTimestamp');
      } finally {
        setIsInitialized(true);
      }
    };

    loadUserFromStorage();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Hardcoded credentials check
    if (email === 'admin@growaton.com' && password === 'Test123456789') {
      const userData: User = {
        email: email,
        name: 'Admin User'
      };
      
      // Save to state and localStorage
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('authTimestamp', Date.now().toString());
      
      return true;
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('authTimestamp');
  };

  const isAuthenticated = user !== null;

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated
  };

  // Don't render children until we've checked localStorage
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 